/** src/components/blocks/AnchorMenu.js */
// Import Core
import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
// Import Libraries
import { AnchorLink } from "gatsby-plugin-anchor-links";
//import Icons
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";

/* Styled Components */
const AnchorMenuStyles = styled.section`
  display: flex;
  justify-content: space-evenly;
  align-items: flex-start;
  padding: 12px 24px;
  gap: 8px 0px;
  margin: 0 auto;
  overflow-x: auto;
  border-top: 1px solid var(--neutral-10);
  border-bottom: 1px solid var(--neutral-10);
  background: var(--white);
  position: sticky;
  top: 177px;
  width: 100%;
  z-index: 2;
  font-family: var(--font-family-heading);
  &::-webkit-scrollbar {
    display: none;
  }
  @media (max-width: 992px) {
    top: calc(83px + 73px);
    justify-content: space-between;
  }
  @media (max-width: 768px) {
  }
  a {
    color: var(--black);
    white-space: nowrap;
    text-decoration: none;
    text-transform: uppercase;
    font-family: var(--font-family-heading);
    transition: all 0.3s ease-in-out;
    display: flex;
    padding: 0px 14px;
    align-items: flex-start;
    gap: 10px;
    &.active {
      color: var(--red);
    }
    &:hover {
      cursor: pointer;
      color: var(--red-110);
    }
  }
`;

const AnchoredMenuScrollArrowsContainer = styled.div`
  position: sticky;
  top: 177px;
  background: var(--white);
  height: fit-content;
  z-index: 5;
  &::-webkit-scrollbar {
    display: none;
  }
  @media (max-width: 992px) {
    top: calc(83px + 73px);
  }

  .arrow {
    position: absolute;
    /* display: flex; */
    justify-content: center;
    align-items: center;
    padding-inline: 10px;
    z-index: 3;
    top: 0;
    font-size: 2rem;
    transition: opacity 0.2s ease-in-out;
    background-color: white;
    opacity: 0;
    border: 1px solid var(--neutral-10);
    height: 100%;
    color: black;
    display: flex;

    &.visible {
      opacity: 1;
    }
    &.invisible {
      opacity: 0;
    }
  }

  .left-arrow {
    left: 0px;
  }

.right-arrow {
    right: 0px;
  }
`;

/* Render Component */
const AnchorMenu = ({ anchorLinks, setAnchorLinks, location }) => {
  // console.log(
  //   "🚀 ~ file: AnchorMenu.js:54 ~ AnchorMenu ~ anchorLinks:",
  //   anchorLinks
  // );
  const [activeMenuIndex, setActiveMenuIndex] = useState(0);
  const scrollableRef = useRef(null);
  const [anchorToShow, setAnchorToShow] = useState(0);
  const [initialLoad, setInitialLoad] = useState(true);
  const [leftArrowVisible, setLeftArrowVisible] = useState(false);
  const [rightArrowVisible, setRightArrowVisible] = useState(false);
 
  const handleArrowClick = (direction) => {
    const scrollArray = Array.from(
      scrollableRef.current.getElementsByTagName("a")
    );

    if (direction === "prev") {
      if (anchorToShow > 0) {
        scrollArray[anchorToShow - 1].scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
        setAnchorToShow(anchorToShow - 1);
      }
    } else if (direction === "next") {
      if (anchorToShow < anchorLinks.length - 1) {
        scrollArray[anchorToShow + 1].scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
        setAnchorToShow(anchorToShow + 1);
      }
    }
  };

  useEffect(() => {
    if (initialLoad) {
      setInitialLoad(false);
      return;
    }
    const isAtStart = scrollableRef?.current?.scrollLeft === 0;
    const isAtEnd =
      scrollableRef?.current?.scrollWidth -
        scrollableRef?.current?.clientWidth <=
      scrollableRef?.current?.scrollLeft;
    setLeftArrowVisible(!isAtStart);
    setRightArrowVisible(!isAtEnd);

    const handleScroll = () => {
      const isAtStart = scrollableRef.current.scrollLeft === 0;
      const isAtEnd =
        scrollableRef.current.scrollWidth - scrollableRef.current.clientWidth <=
        scrollableRef.current.scrollLeft;
      setLeftArrowVisible(!isAtStart);
      setRightArrowVisible(!isAtEnd);
    };
    scrollableRef.current?.addEventListener("scroll", handleScroll);

    return () =>
      scrollableRef.current?.removeEventListener("scroll", handleScroll);
  }, [initialLoad]);

  // On component mount - call function once
  useEffect(() => {
    // Get all ids containing "anchor_" and create an object for each one with 'to' and 'link'
    const getAnchorMenuLinks = () => {
      const allWithClass = Array.from(
        document.getElementsByClassName("anchor-link")
      );

      let tempArr = [];
      allWithClass.forEach((link) => {
        const title = link
          .getAttribute("id")
          .replace("anchor_", "")
          .replaceAll("-", " ");
        const id = `${location.pathname}#${link.getAttribute("id")}`;
        tempArr.push({ to: id, title: (title && title.includes("whats included")) ?  "what's included" : title});
      });
      // console.log("render", anchorLinks, tempArr);
      // If tempArr is same length as anchorLinks don't update again
      if (tempArr.length !== anchorLinks.length) {
        setAnchorLinks(tempArr);
      }
    };

    getAnchorMenuLinks();
  }, [anchorLinks, setAnchorLinks, location.pathname]);
  // Map through anchorLinks array and create anchor link for each one
  return (
    <>
      {anchorLinks?.length > 0 && (
        <AnchoredMenuScrollArrowsContainer>
          <div
            className={`arrow left-arrow ${
              leftArrowVisible ? "visible" : "invisible"
            }`}
            onClick={() => handleArrowClick("prev")}
          >
            <IoIosArrowBack />
          </div>

          <AnchorMenuStyles ref={scrollableRef}>
            {anchorLinks?.map((link, index) => {
              return (
                <AnchorLink
                  key={link.to}
                  to={link.to}
                  title={link.title}
                  className={`small ${
                    activeMenuIndex === index ? " active" : ""
                  }`}
                  onAnchorLinkClick={() => setActiveMenuIndex(index)}
                />
              );
            })}
          </AnchorMenuStyles>

          <div
            className={`arrow right-arrow ${
              rightArrowVisible ? "visible" : "invisible"
            }`}
            onClick={() => handleArrowClick("next")}
          >
            <IoIosArrowForward />
          </div>
        </AnchoredMenuScrollArrowsContainer>
      )}
    </>
  );
};

export default AnchorMenu;
